import { type ReactNode } from 'react';

/**
 * Whether `children` paints nothing — React's own semantics, without the
 * per-render allocation of `Children.toArray`.
 *
 * `null`, `undefined` and booleans are dropped by React; an empty string
 * produces zero DOM (`renderToStaticMarkup(<div>{''}</div>)` is `'<div></div>'`),
 * so it is an unfilled slot rather than content. `0` paints `"0"` and is not
 * empty — that is the case CPMT-5787 exists to fix.
 *
 * Recursing through arrays is what catches an empty `.map()` result and a list
 * whose entries are all empty — both truthy, so a plain `!children` check
 * renders an empty element for them. `.every` short-circuits on the first real
 * child, so a populated list costs one comparison.
 *
 * Non-array iterables (a `Set`, a generator) are valid `ReactNode`s that React
 * flattens, and are reported as non-empty: materialising one to look inside is
 * the allocation this predicate exists to avoid. An element whose own children
 * are empty (an empty Fragment) is likewise not detected — that would mean
 * reading into the element rather than the slot.
 */
export const isEmptyChildren = (children: ReactNode): boolean =>
  children == null ||
  typeof children === 'boolean' ||
  children === '' ||
  (Array.isArray(children) && children.every(isEmptyChildren));
